// UnlockInteractions — the "dots" task board, shown in the left Tools drawer
// (under the Voice feature). It VISUALLY TRACKS interaction tokens; earning
// happens through real actions elsewhere (asking, favoriting, bookmarking,
// adding to home screen, sharing, being a creator/affiliate). When the pool is
// empty it offers the two ways forward: tip (resets the board) or subscribe.
import { LockOpen, Coins, Lock } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { TaskRow } from "@/components/InteractionDots";

const fmtPrice = (cents) => {
  const d = (cents || 0) / 100;
  return `$${d.toFixed(d % 1 === 0 ? 0 : 2)}`;
};

export default function UnlockInteractions({
  interactions, onTip, onSubscribe, onFavorite, onAddHomeScreen, signedIn,
}) {
  if (!interactions || interactions.mode === "unlimited") return null;

  const { mode, board = [], available = 0, silo_price_cents = 0 } = interactions;
  const dollar = interactions.dollar;
  const empty = available <= 0;

  return (
    <div className="mt-4 rounded-md border hairline px-3 py-3" data-testid="unlock-interactions-module">
      <div className="flex items-center gap-2 mb-1">
        <LockOpen size={16} weight="duotone" className="text-[#4A5D4E]" />
        <span className="text-sm font-semibold text-[#1C1B1A]">Unlock interactions</span>
        {mode === "dots" && (
          <span
            data-testid="unlock-available-pill"
            className={`ml-auto text-[10px] font-bold tracking-[0.12em] uppercase px-2 py-0.5 rounded-full ${
              empty ? "bg-[#FDF1ED] text-[#C16E5A]" : "bg-[#EAF1EC] text-[#2F6B3A]"
            }`}
          >
            {available} left
          </span>
        )}
      </div>

      {mode === "dollar" && dollar && (
        <div className="mb-2 rounded-md bg-[#EAF1EC] px-2.5 py-2" data-testid="unlock-dollar-banner">
          <p className="text-[11px] text-[#2F6B3A] font-semibold">You're subscribed — ask freely.</p>
          <p className="text-[10px] text-[#4A5D4E] mt-0.5 leading-snug">
            Your dots below are your safety net if you ever pause your subscription.
          </p>
        </div>
      )}

      {mode === "dots" && (
        <p className="text-[11px] text-[#6B6A68] mb-1.5 leading-snug">
          Each filled dot is one question. Earn more by doing the steps below.
        </p>
      )}

      {mode === "dollar" ? (
        // Subscribed — the board stays VISIBLE but dimmed: tasks still fill in
        // as little wins even though questions aren't being spent. A thin,
        // gently-pulsing sea-green "Subscribed" sits at a tilt across it so the
        // viewer understands WHY their dots aren't disappearing.
        <div className="relative" data-testid="unlock-subscribed-board">
          <div className="divide-y divide-[#F0F1EE] opacity-40 pointer-events-none select-none">
            {board.map((t) => <TaskRow key={t.key} task={t} />)}
          </div>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <motion.span
              animate={{ opacity: [0.5, 0.95, 0.5] }}
              transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
              className="-rotate-[16deg] text-base font-semibold tracking-[0.18em] uppercase text-[#3F7A4E] border-b border-[#3F7A4E]/40 pb-0.5"
              data-testid="unlock-subscribed-stamp"
            >
              Subscribed
            </motion.span>
          </div>
        </div>
      ) : (
        <div className="divide-y divide-[#F0F1EE]">
          {board.map((t) => <TaskRow key={t.key} task={t} />)}
        </div>
      )}

      {/* Earn shortcuts */}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {!signedIn && (
          <button
            onClick={onFavorite}
            data-testid="unlock-favorite-btn"
            className="text-[11px] px-2.5 py-1 rounded-full border hairline bg-white hover:border-[#4A5D4E] text-[#1C1B1A]"
          >
            {/* Jun 2026 (Randy) — it used to read "Favorite & sign up free",
                because the dot only arrived with the account. The star now
                pays on its own, so the button promises only what it does. */}
            {(board.find((t) => t.key === "favorite")?.earned || 0) > 0
              ? "★ Favorited"
              : "★ Favorite this silo"}
          </button>
        )}
        <button
          onClick={onAddHomeScreen}
          data-testid="unlock-homescreen-btn"
          className="text-[11px] px-2.5 py-1 rounded-full border hairline bg-white hover:border-[#4A5D4E] text-[#1C1B1A]"
        >
          + Add to Home Screen
        </button>
      </div>

      {/* When the pool runs dry: the two ways forward */}
      {mode === "dots" && empty && (
        <div className="mt-3 pt-3 border-t hairline space-y-2" data-testid="unlock-empty-cta">
          <p className="text-[11px] text-[#6B6A68] leading-snug">
            Out of questions for now. Keep going:
          </p>
          <button
            onClick={onTip}
            data-testid="unlock-tip-reset-btn"
            className="w-full text-[12px] font-semibold px-3 py-2 rounded-md bg-[#C16E5A] text-white hover:bg-[#a85946] flex items-center justify-center gap-1.5"
          >
            <Coins size={14} weight="fill" /> Tip to reset the board
          </button>
          {silo_price_cents > 0 && (
            <button
              onClick={onSubscribe}
              data-testid="unlock-subscribe-btn"
              className="w-full text-[12px] font-semibold px-3 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] text-[#1C1B1A] flex items-center justify-center gap-1.5"
            >
              <Lock size={14} weight="duotone" /> Subscribe · {fmtPrice(silo_price_cents)}/mo
            </button>
          )}
        </div>
      )}
    </div>
  );
}
